import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Bug,
  ChevronLeft,
  Clock,
  Loader2,
  MoreHorizontal,
  Package,
  Shield,
  Zap,
} from 'lucide-react';
import { usersApi, DeletionReason } from '@/lib/users';

/**
 * Three-step deletion modal:
 *   0 — Why are you leaving? (one radio card per reason)
 *   1 — Optional free-text "What could we have done?"
 *   2 — Password confirmation + final destructive action
 *
 * Ported in spirit from fluxez deletion-exit-survey.tsx — adapted to CadencEZ
 * (in-app modal primitive, single-shot DELETE call carrying the survey
 * payload). The fluxez flow had a separate fire-and-forget feedback
 * endpoint to capture "user backed out at step 2"; we don't ship that here
 * to keep the backend surface small. The audit row on the actual delete
 * carries the same reason + feedback fields.
 */

interface ReasonOption {
  value: DeletionReason;
  labelKey: string;
  Icon: typeof Bug;
  iconClass: string;
}

const REASONS: ReasonOption[] = [
  { value: 'found_alternative', labelKey: 'account.delete.reason.found_alternative', Icon: Package,         iconClass: 'text-blue-500' },
  { value: 'privacy_concerns',  labelKey: 'account.delete.reason.privacy_concerns',  Icon: Shield,          iconClass: 'text-orange-500' },
  { value: 'bugs_errors',       labelKey: 'account.delete.reason.bugs_errors',       Icon: Bug,             iconClass: 'text-red-500' },
  { value: 'missing_features',  labelKey: 'account.delete.reason.missing_features',  Icon: Zap,             iconClass: 'text-purple-500' },
  { value: 'too_complicated',   labelKey: 'account.delete.reason.too_complicated',   Icon: MoreHorizontal,  iconClass: 'text-teal-500' },
  { value: 'not_using',         labelKey: 'account.delete.reason.not_using',         Icon: Clock,           iconClass: 'text-gray-500' },
  { value: 'other',             labelKey: 'account.delete.reason.other',             Icon: MoreHorizontal,  iconClass: 'text-gray-400' },
];

export function DeleteAccountSurvey({
  open,
  onClose,
  onDeleted,
  hasPassword,
}: {
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
  hasPassword: boolean;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [reason, setReason] = useState<DeletionReason | null>(null);
  const [feedback, setFeedback] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const reset = () => {
    setStep(0);
    setReason(null);
    setFeedback('');
    setPassword('');
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
    setTimeout(reset, 250);
  };

  const handleDelete = async () => {
    if (!reason) return;
    setSubmitting(true);
    setError(null);
    try {
      await usersApi.deleteAccount({ password, reason, feedbackText: feedback.trim() || undefined });
      onDeleted();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error).message;
      setError(msg);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-ink-100">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              {step > 0 && (
                <button
                  onClick={() => setStep((step - 1) as 0 | 1)}
                  disabled={submitting}
                  className="rounded-md p-1 text-ink-500 hover:bg-ink-100 disabled:opacity-50"
                  aria-label={t('common.back')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )}
              <div>
                <h2 className="text-base font-semibold text-ink-900">
                  {t('account.delete.title')}
                </h2>
                <p className="text-[12px] text-ink-500 mt-0.5">
                  {t('account.delete.step', { current: step + 1, total: 3 })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {step === 0 && (
            <>
              <p className="text-sm text-ink-700">{t('account.delete.step0_intro')}</p>
              <fieldset className="mt-4 space-y-2">
                {REASONS.map(({ value, labelKey, Icon, iconClass }) => {
                  const selected = reason === value;
                  return (
                    <label
                      key={value}
                      className={
                        'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors ' +
                        (selected
                          ? 'border-accent-500 bg-accent-50'
                          : 'border-ink-200 hover:border-ink-400 hover:bg-ink-50')
                      }
                    >
                      <input
                        type="radio"
                        name="deletion-reason"
                        value={value}
                        checked={selected}
                        onChange={() => setReason(value)}
                        className="sr-only"
                      />
                      <Icon className={`h-5 w-5 ${iconClass}`} strokeWidth={1.8} />
                      <span className="text-sm text-ink-900">{t(labelKey)}</span>
                    </label>
                  );
                })}
              </fieldset>
            </>
          )}

          {step === 1 && (
            <>
              <p className="text-sm text-ink-700">{t('account.delete.step1_intro')}</p>
              <label className="block mt-4">
                <span className="block text-xs font-semibold text-ink-700 mb-1">
                  {t('account.delete.feedback_label')}
                </span>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={5}
                  maxLength={2000}
                  placeholder={t('account.delete.feedback_placeholder')}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/40 focus:border-accent-500"
                />
                <span className="block mt-1 text-[11px] text-ink-400 text-right num">
                  {feedback.length}/2000
                </span>
              </label>
            </>
          )}

          {step === 2 && (
            <>
              <div className="rounded-xl border border-warn-500/30 bg-warn-500/10 px-4 py-3 text-sm text-warn-700">
                <p className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.8} />
                  <span>{t('account.delete.step2_warning')}</span>
                </p>
              </div>
              <ul className="mt-4 text-sm text-ink-700 space-y-1.5 list-disc pl-5">
                <li>{t('account.delete.step2_bullet_1')}</li>
                <li>{t('account.delete.step2_bullet_2')}</li>
                <li>{t('account.delete.step2_bullet_3')}</li>
              </ul>
              {!hasPassword ? (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
                  {t('account.delete.no_password_warning')}
                </div>
              ) : (
                <label className="block mt-4">
                  <span className="block text-xs font-semibold text-ink-700 mb-1">
                    {t('account.delete.password_label')}
                  </span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('account.delete.password_placeholder')}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-warn-500/40 focus:border-warn-500"
                  />
                </label>
              )}
              {error && (
                <p className="mt-3 text-sm text-warn-700">{error}</p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-ink-100 bg-ink-50 flex justify-end gap-2">
          <button
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-ink-300 text-sm text-ink-700 hover:bg-white disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          {step === 0 && (
            <button
              onClick={() => setStep(1)}
              disabled={!reason}
              className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60"
            >
              {t('common.next')}
            </button>
          )}
          {step === 1 && (
            <button
              onClick={() => setStep(2)}
              className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600"
            >
              {t('common.next')}
            </button>
          )}
          {step === 2 && (
            <button
              onClick={handleDelete}
              disabled={submitting || !hasPassword || !password}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-warn-500 text-white text-sm font-semibold hover:bg-warn-600 disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t('account.delete.confirm_button')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
